import { Button, ToggleGroup, ToggleGroupButton, Typography, WithTooltip } from '@compass/components'
import { CalendarIcon, ChevronsUpDownIcon, ListFilterPlusIcon } from 'lucide-react'

import { BOT_DETECTION_SOURCE_LABELS, BOT_DETECTION_SOURCE_TOOLTIPS } from '../consts'
import { BotDetectionMetricCards } from './BotDetectionMetricCards'

const CHART_Y_AXIS_LABELS = ['50K', '40K', '30K', '20K', '10K', '0'] as const
const CHART_GRANULARITIES = ['Hourly', 'Daily'] as const

// A fully static, non-interactive replica of the Overview page used as a backdrop behind
// `BotDetectionUpsellModal` for workspaces without Bot Detection.
export function BotDetectionOverviewSkeleton({ showControls = true }: { showControls?: boolean }) {
  return (
    <div className='flex flex-col gap-4'>
      {showControls && (
        <div className='flex flex-col gap-3 sm:flex-row'>
          <Button variant='secondary' isDisabled>
            <ListFilterPlusIcon />
            Filters
          </Button>
          <Button variant='secondary' isDisabled>
            <CalendarIcon />
            Today
          </Button>
          <ToggleGroup
            size='default'
            selectionMode='single'
            tone='background2'
            isDisabled
            selectedKeys={new Set(['edge'])}
          >
            {Object.entries(BOT_DETECTION_SOURCE_LABELS).map(([id, label]) => (
              <WithTooltip
                key={id}
                content={BOT_DETECTION_SOURCE_TOOLTIPS[id as keyof typeof BOT_DETECTION_SOURCE_TOOLTIPS]}
              >
                <ToggleGroupButton id={id}>{label}</ToggleGroupButton>
              </WithTooltip>
            ))}
          </ToggleGroup>
        </div>
      )}
      <div className='flex flex-col overflow-hidden gap-px bg-gray-300 border rounded-lg'>
        <div className='flex overflow-x-auto overscroll-x-none gap-px lg:grid lg:grid-cols-3'>
          <BotDetectionMetricCards isLoading selectedChart='totalDetections' onSelectChart={() => {}} />
        </div>
        <div className='flex flex-col gap-4 p-4 bg-white'>
          <div className='flex items-start justify-between gap-4'>
            <div>
              <Button variant='ghost' size='sm' isDisabled className='-ml-2 text-base'>
                Total bots
                <ChevronsUpDownIcon />
              </Button>
              <Typography as='p' className='mt-1 text-gray-800'>
                Shows detected bots over time, split by category.
              </Typography>
            </div>
            <ToggleGroup size='sm' isDisabled>
              {CHART_GRANULARITIES.map((x) => {
                return <ToggleGroupButton key={x}>{x}</ToggleGroupButton>
              })}
            </ToggleGroup>
          </div>
          <div className='flex flex-col justify-between w-full h-72 pb-5 font-mono text-xs text-gray-800'>
            {CHART_Y_AXIS_LABELS.map((label) => (
              <span key={label} className='flex items-center gap-2'>
                {label}
                <svg height='1' width='100%' xmlns='http://www.w3.org/2000/svg'>
                  <line
                    x1='0'
                    y1='0.5'
                    x2='100%'
                    y2='0.5'
                    stroke='hsl(var(--fpds-color-gray-3))'
                    strokeWidth='1'
                    strokeDasharray='4 4'
                  />
                </svg>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
